'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import useLocalization from '@/hooks/useLocalization';
import { ClientAuthService } from '@/services/client';
import { Dialog, Icon, Text, HStack } from '@ui-library';
import { Clock4 } from '@ui-library/icons';

const WARNING_THRESHOLD_SECONDS = 15;

type SessionTimeoutProps = {
  timeoutSeconds: number;
  company: string;
  guestBookSlug: string;
  isVisitor?: boolean;
};

type TimerHandles = {
  timeout: ReturnType<typeof setTimeout> | null;
  warning: ReturnType<typeof setTimeout> | null;
  countdown: ReturnType<typeof setInterval> | null;
};

export const SessionTimeout = ({
  timeoutSeconds,
  company,
  guestBookSlug,
  isVisitor = false,
}: SessionTimeoutProps) => {
  const router = useRouter();
  const intl = useIntl();
  const { locale } = useLocalization();

  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    WARNING_THRESHOLD_SECONDS,
  );

  // Create a ref to always track the current state of showWarning
  const showWarningRef = useRef(showWarning);

  // Update the ref whenever the state changes
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const timersRef = useRef<TimerHandles>({
    timeout: null,
    warning: null,
    countdown: null,
  });

  const resetTimers = useCallback(() => {
    if (timersRef.current.timeout) {
      clearTimeout(timersRef.current.timeout);
      timersRef.current.timeout = null;
    }
    if (timersRef.current.warning) {
      clearTimeout(timersRef.current.warning);
      timersRef.current.warning = null;
    }

    if (timersRef.current.countdown) {
      clearInterval(timersRef.current.countdown);
      timersRef.current.countdown = null;
    }
  }, []);

  const performLogout = useCallback(() => {
    resetTimers();
    setShowWarning(false);
    ClientAuthService.kioskLogout();
    router.push(
      `/${locale}/${company}/${guestBookSlug}/kiosk?inactivityPeriod=${timeoutSeconds}`,
    );
  }, [router, locale, company, guestBookSlug, resetTimers, timeoutSeconds]);

  const startCountdown = useCallback(() => {
    console.log('Starting countdown for session timeout');
    setRemainingSeconds(WARNING_THRESHOLD_SECONDS);
    setShowWarning(true);

    if (timersRef.current.countdown) {
      clearInterval(timersRef.current.countdown);
      timersRef.current.countdown = null;
    }
    const countdownInterval = setInterval(() => {
      if (document.hidden) {
        return;
      }

      setRemainingSeconds((prevSeconds) => {
        return prevSeconds - 1;
      });
    }, 1000);

    timersRef.current.countdown = countdownInterval;

    timersRef.current.timeout = setTimeout(() => {
      performLogout();
    }, WARNING_THRESHOLD_SECONDS * 1000);
  }, [performLogout]);

  const setupInactivityTimer = useCallback(() => {
    console.log('Resetting timer and setting up inactivity timer');
    resetTimers();
    const warningTime = (timeoutSeconds - WARNING_THRESHOLD_SECONDS) * 1000;
    const warningTimer = setTimeout(() => {
      startCountdown();
    }, warningTime);

    timersRef.current.warning = warningTimer;
  }, [resetTimers, timeoutSeconds, startCountdown]);

  const continueSession = useCallback(() => {
    setShowWarning(false);
    setupInactivityTimer();
  }, [setupInactivityTimer]);

  const handleActivity = useCallback(() => {
    console.log('activity detected');
    const isWarningShowing = showWarningRef.current;
    if (!isWarningShowing) {
      setupInactivityTimer();
    }
  }, [setupInactivityTimer]);

  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'popstate',
    ];

    setupInactivityTimer();

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      handleActivity();
      return originalPushState.apply(this, args);
    };

    history.replaceState = function (...args) {
      handleActivity();
      return originalReplaceState.apply(this, args);
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      resetTimers();

      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;

      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity, resetTimers, setupInactivityTimer]);

  return (
    <>
      {showWarning && (
        <Dialog
          open={showWarning}
          onOpenChange={({ open }) => {
            if (!open) continueSession();
          }}
          variant="info"
          title={intl.formatMessage({
            id: 'kiosk.sessionTimeout.title',
            defaultMessage: 'Session inactivity',
          })}
          size="md"
          placement="center"
          saveAction={continueSession}
          cancelAction={performLogout}
          cancelText={
            isVisitor
              ? intl.formatMessage({
                  id: 'kiosk.sessionTimeout.backToStart',
                  defaultMessage: 'Back to start',
                })
              : intl.formatMessage({
                  id: 'kiosk.sessionTimeout.cancel',
                  defaultMessage: 'Sign out',
                })
          }
          saveText={intl.formatMessage({
            id: 'kiosk.sessionTimeout.continue',
            defaultMessage: 'Continue',
          })}
        >
          <>
            <Text>
              {isVisitor
                ? intl.formatMessage({
                    id: 'kiosk.sessionTimeout.visitorInstruction',
                    defaultMessage:
                      'Tap "Continue" to keep browsing. Going back to the start screen in:',
                  })
                : intl.formatMessage({
                    id: 'kiosk.sessionTimeout.instruction',
                    defaultMessage:
                      'Click "continue" to stay signed in. Your session will expire in:',
                  })}
            </Text>

            <HStack mt="4">
              <Icon as={Clock4} color="mint.600" />
              <Text fontSize="2xl" fontWeight="semibold" color="mint.600">
                {remainingSeconds}{' '}
                {intl.formatMessage({
                  id: 'kiosk.sessionTimeout.seconds',
                  defaultMessage: 'Seconds',
                })}
              </Text>
            </HStack>
          </>
        </Dialog>
      )}
    </>
  );
};
